// src/screens/Dashboard.js v1.5
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getOccupancy, getDailyRevenue, getFootfallToday, getExpiringTenants,
  getGhostCars, getActiveVehicles, getSettings, forceExitVehicle,
  getUnsettledMouByCompany, getMouCompanies, calculateLiveFee,
  getTodayCheckpoints, getDailyPL
} from '../database/db';
import { format } from 'date-fns';
import { RefreshCw, AlertTriangle, Mic, Search } from 'lucide-react';

const fmtTime = iso => iso ? format(new Date(iso),'hh:mm a') : '—';
const fmt = n => `AED ${Number(n||0).toFixed(2)}`;
const getVehicleCardColor = (v) => {
  const pkg = v.packageType || 'hourly';
  if(pkg === 'hourly') return 'var(--warning)'; // amber
  if(pkg === 'overnight') return '#8b5cf6'; // purple
  if(pkg === 'mou') return '#06b6d4'; // teal
  if(pkg.startsWith('monthly') || pkg === 'weekly' || pkg === 'biweekly') return 'var(--success)'; // green
  return 'var(--border)';
};

const liveDuration = entryTime => {
  const mins = Math.floor((Date.now() - new Date(entryTime)) / 60000);
  const h = Math.floor(mins/60); const m = mins%60;
  return h>0 ? `${h}h ${m}m` : `${m}m`;
};

// ── STAFF VIEW ────────────────────────────────────────────
function StaffDashboard({ user, navigate }) {
  const [active, setActive] = useState([]);
  const [ghosts, setGhosts] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [parkedSearch, setParkedSearch] = useState('');
  const [listening, setListening] = useState(false);
  const [tick, setTick] = useState(0);
  const [printAlert, setPrintAlert] = useState('');
  const settings = getSettings();

  const printEntryReceipt = async(v) => {
    const device = getPrinterDevice();
    if(!device){setPrintAlert('Printer not connected. Go to Settings → Printer.');setTimeout(()=>setPrintAlert(''),4000);return;}
    try{
      const {printEntryReceipt: printFn} = await import('../utils/bluetooth-printer');
      await printFn(device, v, settings);
      setPrintAlert('✅ Printed!');
    }catch(e){setPrintAlert('❌ '+e.message);}
    setTimeout(()=>setPrintAlert(''),4000);
  };

  const load = useCallback(() => {
    setActive(getActiveVehicles());
    setGhosts(getGhostCars());
    setExpiring(getExpiringTenants(2));
  }, []);

  useEffect(() => {
    load();
    // Instant re-render on every polling update
    const handler = () => {
      const newActive = getActiveVehicles();
      setActive([...newActive]); // spread forces React re-render
      setGhosts(getGhostCars());
      setExpiring(getExpiringTenants(2));
    };
    window.addEventListener('ps_vehicles_updated', handler);
    return () => window.removeEventListener('ps_vehicles_updated', handler);
  }, [load]);
  useEffect(() => { const t = setInterval(()=>setTick(n=>n+1),60000); return()=>clearInterval(t); }, []);

  const handleVoice = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.lang='en-AE';
    r.onstart=()=>setListening(true);
    r.onresult=e=>{setParkedSearch(e.results[0][0].transcript.toUpperCase().replace(/\s/g,''));setListening(false);};
    r.onerror=()=>setListening(false); r.onend=()=>setListening(false);
    r.start();
  };

  const filtered = parkedSearch ? active.filter(v=>v.plate.includes(parkedSearch.toUpperCase())) : active;
  const notFound = parkedSearch && filtered.length===0;
  const found = parkedSearch && filtered.length>0;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">🅿️ Dashboard</div><div className="page-subtitle">{format(new Date(),'dd/MM/yyyy')} · {user.name}</div></div>
        <div className="flex-row"><div className="live-dot"/><span style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>LIVE</span><button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button></div>
      </div>
      <div className="page-body">
        {/* Alerts */}
        {ghosts.length>0&&<div className="alert alert-warning" style={{marginBottom:10}}><AlertTriangle size={14} style={{flexShrink:0}}/><strong>{ghosts.length}</strong> vehicle(s) parked 24h+.</div>}
        {expiring.length>0&&<div className="alert alert-danger" style={{marginBottom:10}}><AlertTriangle size={14} style={{flexShrink:0}}/><strong>{expiring.length}</strong> package(s) expiring soon.<button className="btn btn-ghost btn-sm" style={{marginLeft:8}} onClick={()=>navigate('/tenants')}>View</button></div>}

        {printAlert&&<div className={`alert ${printAlert.startsWith('✅')?'alert-success':'alert-danger'}`} style={{marginBottom:8}}>{printAlert}</div>}
        {/* BIG ACTION BUTTONS */}
        <div className="quick-actions">
          <button className="quick-action-btn entry" onClick={()=>navigate('/entry')}>
            <span className="quick-action-icon">🚗</span>
            <span className="quick-action-label">ENTRY</span>
            <span className="quick-action-sub">Log car in</span>
          </button>
          <button className="quick-action-btn exit" onClick={()=>navigate('/exit')}>
            <span className="quick-action-icon">🏁</span>
            <span className="quick-action-label">EXIT</span>
            <span className="quick-action-sub">Process & collect</span>
          </button>
        </div>

        {/* CURRENTLY PARKED with search + quick exit */}
        <div className="card">
          <div className="flex-between" style={{marginBottom:10}}>
            <div className="card-title" style={{margin:0}}>🚗 Parked ({active.length}/{getSettings().totalSpots})</div>
            <span style={{fontSize:13,color:active.length>=getSettings().totalSpots?'var(--danger)':'var(--success)',fontWeight:700}}>{getSettings().totalSpots-active.length} free</span>
          </div>
          {/* Occupancy summary for staff */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:12}}>
            {[['Occupied',active.length,'var(--accent)'],['Available',Math.max(0,getSettings().totalSpots-active.length),'var(--success)'],['Hourly',active.filter(v=>!v.tenantId&&!v.isMou).length,'var(--warning)']].map(([l,n,col])=>(
              <div key={l} style={{background:'var(--bg-input)',borderRadius:'var(--radius-sm)',padding:'10px 8px',textAlign:'center'}}>
                <div style={{fontFamily:'var(--font-display)',fontSize:22,fontWeight:800,color:col}}>{n}</div>
                <div style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)',textTransform:'uppercase',marginTop:3}}>{l}</div>
              </div>
            ))}
          </div>
          {/* Colour legend */}
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:10,fontSize:11}}>
            {[['var(--warning)','Hourly'],['var(--success)','Package'],['#8b5cf6','Overnight'],['#06b6d4','MOU']].map(([col,lbl])=>(
              <div key={lbl} style={{display:'flex',alignItems:'center',gap:4}}><div style={{width:10,height:10,borderRadius:2,background:col,flexShrink:0}}/><span style={{color:'var(--text-muted)'}}>{lbl}</span></div>
            ))}
          </div>
          <div style={{position:'relative',marginBottom:10}}>
            <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)'}}/>
            <input className="form-input" style={{paddingLeft:32,paddingRight:72,fontFamily:'var(--font-mono)',letterSpacing:'0.06em'}} placeholder="Search plate..." value={parkedSearch} onChange={e=>setParkedSearch(e.target.value.toUpperCase())}/>
            <div style={{position:'absolute',right:4,top:'50%',transform:'translateY(-50%)',display:'flex',gap:2}}>
              {parkedSearch&&<button onClick={()=>setParkedSearch('')} style={{background:'var(--danger-dim)',border:'none',cursor:'pointer',color:'var(--danger)',padding:'4px 8px',borderRadius:'var(--radius-sm)',fontSize:12,fontWeight:700}}>✕</button>}
              <button onClick={handleVoice} style={{background:'none',border:'none',cursor:'pointer',color:listening?'var(--danger)':'var(--text-muted)',padding:4}}><Mic size={14}/></button>
            </div>
          </div>
          {notFound&&<div className="alert alert-danger">⚠️ <strong>{parkedSearch}</strong> NOT in system.</div>}
          {found&&<div className="alert alert-success">✅ <strong>{parkedSearch}</strong> is parked.</div>}
          {active.length===0
            ?<div className="empty-state"><div className="empty-state-icon">🅿️</div><div className="empty-state-text">No vehicles parked</div></div>
            :<div style={{display:'flex',flexDirection:'column',gap:6}}>
              {filtered.slice(0,20).map(v=>{
                const liveFee = v.tenantId ? null : calculateLiveFee(v.entryTime, v.hourlyRate||settings.hourlyRate);
                return (
                  <div key={v.id} style={{background:'var(--bg-input)',border:`1px solid var(--border)`,borderLeft:`4px solid ${getVehicleCardColor(v)}`,borderRadius:'var(--radius-sm)',padding:'10px 12px',display:'flex',alignItems:'center',gap:8}}>
                    <div style={{fontFamily:'var(--font-mono)',fontSize:14,fontWeight:700,color:getVehicleCardColor(v),background:`${getVehicleCardColor(v)}20`,padding:'5px 8px',borderRadius:'var(--radius-sm)',minWidth:72,textAlign:'center'}}>{v.plate}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,color:'var(--text-muted)'}}>{v.packageType||'Hourly'}</div>
                      <div style={{fontSize:12,color:'var(--success)',fontWeight:600,fontFamily:'var(--font-mono)'}}>
                        ⏱ {liveDuration(v.entryTime)}{liveFee?` · 💰 ${fmt(liveFee.finalFee)}`:''}
                        {v.tenantId&&<span style={{color:'var(--success)',marginLeft:6,fontSize:10}}>Paid</span>}
                      </div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:4,flexShrink:0}}>
                      <button className="btn btn-warning btn-sm" style={{fontSize:11}} onClick={()=>navigate('/exit',{state:{plate:v.plate}})}>Exit →</button>
                      <button className="btn btn-outline btn-sm" style={{fontSize:10,padding:'4px 6px'}} onClick={()=>printEntryReceipt(v)}>🖨️</button>
                    </div>
                  </div>
                );
              })}
              {filtered.length>20&&<div style={{textAlign:'center',fontSize:12,color:'var(--text-muted)'}}>+{filtered.length-20} more</div>}
            </div>
          }
        </div>
      </div>
    </div>
  );
}

// ── ADMIN VIEW ────────────────────────────────────────────
function AdminDashboard({ user, navigate }) {
  const [data, setData] = useState(null);
  const [ghostModal, setGhostModal] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const [selectedGhost, setSelectedGhost] = useState(null);
  const [parkedSearch, setParkedSearch] = useState('');
  const [listening, setListening] = useState(false);
  const [tick, setTick] = useState(0);
  const settings = getSettings();

  const load = useCallback(() => {
    const occ=getOccupancy(); const rev=getDailyRevenue(); const footfall=getFootfallToday();
    const expiring=getExpiringTenants(2); const ghosts=getGhostCars(); const active=getActiveVehicles();
    const mou=getMouCompanies();
    const mouRec=mou.map(m=>({...m,unsettled:getUnsettledMouByCompany(m.id),totalDue:getUnsettledMouByCompany(m.id).reduce((s,r)=>s+(r.fee||0),0)})).filter(m=>m.totalDue>0);
    const todayChk=getTodayCheckpoints(); const pl=getDailyPL();
    setData({occ,rev,footfall,expiring,ghosts,active,mouRec,todayChk,pl});
  }, []);

  useEffect(()=>{
    load();
    // Instant re-render on every polling update
    const handler = () => {
      // Update active vehicles immediately without full reload
      const newActive = getActiveVehicles();
      setData(prev => prev ? {...prev, active:[...newActive], occ:getOccupancy()} : prev);
    };
    window.addEventListener('ps_vehicles_updated', handler);
    return()=>{window.removeEventListener('ps_vehicles_updated', handler);};
  },[load]);
  useEffect(()=>{const t=setInterval(()=>setTick(n=>n+1),60000);return()=>clearInterval(t);},[]);

  if (!data) return <div className="loading-screen"><div className="spinner"/></div>;
  const {occ,rev,footfall,expiring,ghosts,active,mouRec,todayChk,pl} = data;

  const confirmForceExit = () => {
    if (!forceReason.trim()) return;
    forceExitVehicle(selectedGhost.id, user.id, user.name, forceReason);
    setGhostModal(false); setForceReason(''); setSelectedGhost(null); load();
  };

  const handleVoice = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.lang='en-AE';
    r.onstart=()=>setListening(true);
    r.onresult=e=>{setParkedSearch(e.results[0][0].transcript.toUpperCase().replace(/\s/g,''));setListening(false);};
    r.onerror=()=>setListening(false); r.onend=()=>setListening(false);
    r.start();
  };

  const filtered = parkedSearch ? active.filter(v=>v.plate.includes(parkedSearch.toUpperCase())) : active;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">🅿️ Dashboard</div><div className="page-subtitle">{settings.businessName} · {format(new Date(),'EEEE, dd/MM/yyyy')}</div></div>
        <div className="flex-row"><div className="live-dot"/><span style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>LIVE</span><button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button></div>
      </div>
      <div className="page-body">
        {ghosts.length>0&&<div className="alert alert-warning" style={{marginBottom:10}}><AlertTriangle size={14} style={{flexShrink:0}}/><div><strong>{ghosts.length}</strong> vehicle(s) 24h+.<button className="btn btn-ghost btn-sm" style={{marginLeft:8}} onClick={()=>setGhostModal(true)}>Review</button></div></div>}
        {expiring.length>0&&<div className="alert alert-danger" style={{marginBottom:10}}><AlertTriangle size={14} style={{flexShrink:0}}/><strong>{expiring.length}</strong> package(s) expiring soon.<button className="btn btn-ghost btn-sm" style={{marginLeft:8}} onClick={()=>navigate('/tenants')}>View</button></div>}

        <div className="quick-actions">
          <button className="quick-action-btn entry" onClick={()=>navigate('/entry')}><span className="quick-action-icon">🚗</span><span className="quick-action-label">ENTRY</span><span className="quick-action-sub">Log car in</span></button>
          <button className="quick-action-btn exit" onClick={()=>navigate('/exit')}><span className="quick-action-icon">🏁</span><span className="quick-action-label">EXIT</span><span className="quick-action-sub">Process & collect</span></button>
        </div>

        {/* OCCUPANCY */}
        <div className="occupancy-grid">
          {[{n:occ.occupied,l:'🚗 Occupied',c:'var(--accent)'},{n:occ.available,l:'✅ Available',c:'var(--success)'},{n:occ.open,l:'◯ Open',c:'var(--accent2)'},{n:occ.closed,l:'● Closed',c:'var(--warning)'}].map(b=>(
            <div key={b.l} className="occ-box" style={{borderTop:`3px solid ${b.c}`}}><div className="occ-number" style={{color:b.c}}>{b.n}</div><div className="occ-label">{b.l}</div></div>
          ))}
        </div>

        {/* REVENUE */}
        <div className="stat-grid">
          {[{label:'Cash Today',value:fmt(rev.totalCash),icon:'💵',cls:'green'},{label:'Card Today',value:fmt(rev.totalCard),icon:'💳',cls:'blue'},{label:'MOU Today',value:fmt(rev.totalMou),icon:'🤝',cls:'purple'},{label:'Total Revenue',value:fmt(rev.total),icon:'💰',cls:'gold'}].map(s=>(
            <div key={s.label} className={`stat-box ${s.cls}`}><span className="stat-icon">{s.icon}</span><div className="stat-label">{s.label}</div><div className="stat-value">{s.value}</div></div>
          ))}
        </div>

        {/* DAILY P&L */}
        <div className="card" style={{marginBottom:12}}>
          <div className="card-title">Today's P&L</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
            {[['Revenue',pl.revenue,'var(--success)'],['Expenses',pl.expenses,'var(--danger)'],['Net Profit',pl.netProfit,pl.netProfit>=0?'var(--success)':'var(--danger)']].map(([l,v,c])=>(
              <div key={l} style={{textAlign:'center',padding:'10px 8px',background:'var(--bg-input)',borderRadius:'var(--radius-sm)'}}>
                <div style={{fontSize:9,color:'var(--text-muted)',fontFamily:'var(--font-mono)',textTransform:'uppercase',marginBottom:4}}>{l}</div>
                <div style={{fontFamily:'var(--font-display)',fontSize:16,fontWeight:700,color:c}}>{fmt(v)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FOOTFALL */}
        <div className="card" style={{marginBottom:12}}>
          <div className="card-title">Today's Footfall</div>
          <div className="footfall-grid">
            {[{n:footfall.total,l:'Total',c:'var(--accent)',e:'🏁'},{n:footfall.fixed,l:'Fixed Pkg',c:'var(--success)',e:'📋'},{n:footfall.hourly,l:'Hourly',c:'var(--accent2)',e:'⏱️'},{n:footfall.mou,l:'MOU',c:'var(--mou)',e:'🤝'}].map(f=>(
              <div key={f.l} className="footfall-item"><div className="num" style={{color:f.c}}>{f.e} {f.n}</div><div className="lbl">{f.l}</div></div>
            ))}
          </div>
        </div>

        {/* CASH CHECKPOINTS TODAY */}
        {todayChk.length>0&&(
          <div className="card" style={{marginBottom:12}}>
            <div className="card-title">Today's Cash Checkpoints</div>
            {todayChk.map(c=>(
              <div key={c.id} className="flex-between" style={{marginBottom:6,padding:'8px 10px',background:'var(--bg-input)',borderRadius:'var(--radius-sm)'}}>
                <div><span style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{format(new Date(c.timestamp),'HH:mm')}</span><span style={{fontSize:11,color:'var(--text-secondary)',marginLeft:8}}>{c.type} · {c.staffName}</span></div>
                <span style={{fontFamily:'var(--font-mono)',color:'var(--success)',fontWeight:700,fontSize:13}}>AED {c.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {/* MOU RECEIVABLES */}
        {mouRec.length>0&&(
          <div className="card" style={{marginBottom:12}}>
            <div className="flex-between" style={{marginBottom:10}}><div className="card-title" style={{margin:0}}>🤝 MOU Receivables</div><span className="badge badge-warning">PENDING</span></div>
            {mouRec.map(m=>(
              <div key={m.id} className="flex-between" style={{padding:'8px 10px',background:'var(--bg-input)',borderRadius:'var(--radius-sm)',marginBottom:6}}>
                <div><div style={{fontWeight:600,fontSize:13}}>{m.companyName}</div><div style={{fontSize:11,color:'var(--text-muted)'}}>{m.unsettled.length} visit(s)</div></div>
                <div style={{fontFamily:'var(--font-mono)',color:'var(--warning)',fontWeight:700}}>AED {m.totalDue.toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}

        {/* CURRENTLY PARKED */}
        <div className="card">
          <div className="flex-between" style={{marginBottom:10}}>
            <div className="card-title" style={{margin:0}}>🚗 Currently Parked ({active.length})</div>
          </div>
          <div style={{position:'relative',marginBottom:10}}>
            <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)'}}/>
            <input className="form-input" style={{paddingLeft:32,paddingRight:72,fontFamily:'var(--font-mono)',letterSpacing:'0.06em'}} placeholder="Search plate..." value={parkedSearch} onChange={e=>setParkedSearch(e.target.value.toUpperCase())}/>
            <div style={{position:'absolute',right:4,top:'50%',transform:'translateY(-50%)',display:'flex',gap:2}}>
              {parkedSearch&&<button onClick={()=>setParkedSearch('')} style={{background:'var(--danger-dim)',border:'none',cursor:'pointer',color:'var(--danger)',padding:'4px 8px',borderRadius:'var(--radius-sm)',fontSize:12,fontWeight:700}}>✕</button>}
              <button onClick={handleVoice} style={{background:'none',border:'none',cursor:'pointer',color:listening?'var(--danger)':'var(--text-muted)',padding:4}}><Mic size={14}/></button>
            </div>
          </div>
          {active.length===0
            ?<div className="empty-state"><div className="empty-state-icon">🅿️</div><div className="empty-state-text">No vehicles parked</div></div>
            :<div style={{display:'flex',flexDirection:'column',gap:6}}>
              {filtered.slice(0,15).map(v=>{
                const liveFee = v.tenantId ? null : calculateLiveFee(v.entryTime, v.hourlyRate||settings.hourlyRate);
                return (
                  <div key={v.id} style={{background:'var(--bg-input)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'10px 12px',display:'flex',alignItems:'center',gap:8}}>
                    <div style={{fontFamily:'var(--font-mono)',fontSize:13,fontWeight:700,color:'var(--accent)',background:'var(--accent-dim)',padding:'4px 8px',borderRadius:'var(--radius-sm)',minWidth:70,textAlign:'center'}}>{v.plate}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,color:'var(--text-muted)'}}>{v.packageType||'Hourly'}{v.isMou&&<span className="badge badge-mou" style={{marginLeft:4,fontSize:9}}>MOU</span>}</div>
                      <div style={{fontSize:11,color:'var(--success)',fontWeight:600,fontFamily:'var(--font-mono)'}}>⏱ {liveDuration(v.entryTime)}{liveFee?` · 💰 ${fmt(liveFee.finalFee)}`:''}{v.tenantId&&<span style={{color:'var(--success)',marginLeft:6,fontSize:10}}>Paid</span>}</div>
                    </div>
                    <div style={{fontSize:12,color:v.carType==='open'?'var(--success)':'var(--warning)',marginRight:4}}>{v.carType==='open'?'◯':'●'}</div>
                    <button className="btn btn-warning btn-sm" style={{flexShrink:0,fontSize:11}} onClick={()=>navigate('/exit',{state:{plate:v.plate}})}>Exit →</button>
                  </div>
                );
              })}
              {filtered.length>15&&<div style={{textAlign:'center',fontSize:12,color:'var(--text-muted)'}}>+{filtered.length-15} more</div>}
            </div>
          }
        </div>
      </div>

      {/* GHOST MODAL */}
      {ghostModal&&(
        <div className="modal-overlay" onClick={()=>setGhostModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">⚠️ Vehicles 24h+</div><button className="btn btn-ghost btn-sm" onClick={()=>setGhostModal(false)}>✕</button></div>
            <div className="modal-body">
              {ghosts.map(v=>(
                <div key={v.id} style={{padding:12,background:'var(--bg-input)',borderRadius:'var(--radius-sm)',marginBottom:8}}>
                  <div className="flex-between"><span style={{fontFamily:'var(--font-mono)',fontWeight:700,color:'var(--accent)'}}>{v.plate}</span><span style={{fontSize:11,color:'var(--danger)'}}>{liveDuration(v.entryTime)}</span></div>
                  <button className="btn btn-danger btn-sm" style={{marginTop:8}} onClick={()=>setSelectedGhost(v)}>Force Clear</button>
                </div>
              ))}
              {selectedGhost&&<div style={{marginTop:12}}>
                <div className="form-group"><label className="form-label">Reason</label><input className="form-input" value={forceReason} onChange={e=>setForceReason(e.target.value)} placeholder="Enter reason..."/></div>
                <button className="btn btn-danger btn-full" onClick={confirmForceExit}>Confirm Force Exit</button>
              </div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  // Change #1 — role-based dashboard
  if (user.role === 'admin') return <AdminDashboard user={user} navigate={navigate}/>;
  return <StaffDashboard user={user} navigate={navigate}/>;
}
